import { useState, useEffect } from "react";
import { toast } from "@/components/ui/sonner";
import { Bell, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { isPushSupported, getPushState, enablePush } from "@/lib/appresse";

const DISMISS_KEY = "appresse_push_prompt_dismissed";

export const NotificationPrompt = () => {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isPushSupported()) return;
    if (localStorage.getItem(DISMISS_KEY)) return;
    let alive = true;
    (async () => {
      const state = await getPushState();
      if (alive && state === "default") {
        setTimeout(() => alive && setShow(true), 3500);
      }
    })();
    return () => { alive = false; };
  }, []);

  const enable = async () => {
    setLoading(true);
    try {
      await enablePush([]);
      toast.success("Notifications activées ✓ Vous serez prévenu des nouveautés.");
      localStorage.setItem(DISMISS_KEY, "1");
      setShow(false);
    } catch (e) {
      const raw = e?.message || "";
      // Axios/network/5xx errors have ugly raw messages — show a friendly one.
      const network = e?.response || /status code|Network Error|timeout|Failed to fetch/i.test(raw);
      toast.error(network ? "Connexion instable, réessayez dans un instant." : (raw || "Impossible d'activer les notifications."));
    } finally {
      setLoading(false);
    }
  };

  const dismiss = () => { localStorage.setItem(DISMISS_KEY, "1"); setShow(false); };

  if (!show) return null;

  return (
    <div className="fixed z-[60] left-3 right-3 bottom-[calc(env(safe-area-inset-bottom)+72px)] sm:left-auto sm:right-4 sm:bottom-4 sm:max-w-sm"
      data-testid="push-prompt">
      <div className="bg-card border border-border rounded-2xl shadow-[0_16px_40px_-10px_rgba(0,0,0,0.5)] p-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <Bell className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-serif font-bold text-sm leading-tight">Restez informé chaque jour</p>
          <p className="text-xs text-muted-foreground mt-0.5">Activez les notifications pour être prévenu dès qu'une nouvelle édition arrive.</p>
          <div className="flex gap-2 mt-2.5">
            <Button size="sm" className="h-8 gap-1.5" onClick={enable} disabled={loading} data-testid="push-enable-button">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />} Activer
            </Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={dismiss} data-testid="push-dismiss-button">Plus tard</Button>
          </div>
        </div>
        <button onClick={dismiss} className="text-muted-foreground hover:text-foreground shrink-0" aria-label="Fermer" data-testid="push-close-button">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
